import {
  useQueries,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useGT } from 'gt-react';
import {
  EXTENSION_TARGET_MODELS,
  getExtensionFieldExposure,
  type ExtensionFieldExposure,
  type ExtensionFieldExposureMap,
} from '@erp/shared-types';
import type { SchemaExtensionFieldExposureResponse } from '@erp/rpc';
import { rpcClient } from 'src/api/rpc/client';
import { useToast } from 'src/context/ToastContext';

/**
 * Entity extensions: a `sql` schema definition registered against an extendable
 * entity, provisioned as a physical table whose columns are its fields.
 */

const QUERY_KEY_BASE = 'schema-extensions';
const SCHEMA_DEFINITIONS_KEY = 'schema-definitions';

export function useSchemaExtensionStatus(schemaDefinitionId: string) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'status', schemaDefinitionId],
    queryFn: () =>
      rpcClient.core.schemaExtensions.status({ schemaDefinitionId }),
    enabled: !!schemaDefinitionId,
  });
}

export function useListSchemaExtensions(targetModel: string) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'list', targetModel],
    queryFn: () => rpcClient.core.schemaExtensions.list({ targetModel }),
    enabled: !!targetModel,
  });
}

/**
 * The entities that already have an extension. An entity takes at most one, so
 * these are the ones a new extension can't target. `isLoading` stays true until
 * every entity has answered, so a caller never offers a taken one early.
 */
export function useExtendedTargetModels(enabled: boolean) {
  const results = useQueries({
    queries: EXTENSION_TARGET_MODELS.map((targetModel) => ({
      queryKey: [QUERY_KEY_BASE, 'list', targetModel],
      queryFn: () => rpcClient.core.schemaExtensions.list({ targetModel }),
      enabled,
    })),
  });
  return {
    extended: new Set(
      EXTENSION_TARGET_MODELS.filter(
        (_targetModel, index) => (results[index]?.data?.length ?? 0) > 0,
      ),
    ),
    isLoading: results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
    refetch: () => {
      for (const result of results) void result.refetch();
    },
  };
}

/**
 * What publishing the current draft would do to the extension table. Drives the
 * destructive-change confirmation before a publish.
 */
export function useSchemaExtensionPublishPreview(schemaDefinitionId: string) {
  return useQuery({
    queryKey: [QUERY_KEY_BASE, 'publish-preview', schemaDefinitionId],
    queryFn: () =>
      rpcClient.core.schemaExtensions.previewPublishSync({
        schemaDefinitionId,
      }),
    enabled: !!schemaDefinitionId,
  });
}

function useExtensionMutation<
  TInput extends { schemaDefinitionId: string },
  TResult,
>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_result, input) => {
      // Registration changes the schema's targetModel and its provisioned state,
      // so both caches are stale.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE] });
      queryClient.invalidateQueries({
        queryKey: [SCHEMA_DEFINITIONS_KEY, 'get', input.schemaDefinitionId],
      });
    },
  });
}

export function useRegisterSchemaExtension() {
  return useExtensionMutation(
    (input: { schemaDefinitionId: string; targetModel: string }) =>
      rpcClient.core.schemaExtensions.register(input),
  );
}

/** Drops the extension table and its data — `confirmation` is the schema's name. */
export function useUnregisterSchemaExtension() {
  return useExtensionMutation(
    (input: { schemaDefinitionId: string; confirmation: string }) =>
      rpcClient.core.schemaExtensions.unregister(input),
  );
}

/** Applies pending column changes to a provisioned extension table. */
export function useSyncSchemaExtension() {
  return useExtensionMutation((input: { schemaDefinitionId: string }) =>
    rpcClient.core.schemaExtensions.sync(input),
  );
}

export type ExposureSwitch = keyof ExtensionFieldExposure;

const fieldExposureKey = (extensionId: string) => [
  QUERY_KEY_BASE,
  'field-exposure',
  extensionId,
];

/**
 * The live exposure policy for an extension's provisioned fields. Gated on
 * `enabled` because the backend refuses it to non-administrators.
 */
export function useSchemaExtensionFieldExposure(
  extensionId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: fieldExposureKey(extensionId ?? ''),
    queryFn: () =>
      rpcClient.core.schemaExtensions.getFieldExposure({
        extensionId: extensionId ?? '',
      }),
    enabled: enabled && !!extensionId,
  });
}

export interface ExposureToggle {
  fieldId: string;
  /** The field's label, for the error toast. */
  label: string;
  exposure: ExposureSwitch;
  value: boolean;
}

/** One switch flipped on an exposure map, leaving every other field as it was. */
function applyExposureToggle(
  fieldExposure: ExtensionFieldExposureMap,
  { fieldId, exposure, value }: ExposureToggle,
): ExtensionFieldExposureMap {
  return {
    ...fieldExposure,
    [fieldId]: {
      ...getExtensionFieldExposure(fieldExposure, fieldId),
      [exposure]: value,
    },
  };
}

/**
 * Flips one exposure switch. Exposure is live — not part of the schema's
 * draft/publish — so the switch moves at once and rolls back if the save fails.
 *
 * Each save sets one switch server-side, so no save can overwrite another
 * switch's change. Saves run one at a time per extension, so the server
 * applies a switch's clicks in click order. Once the queue drains the policy
 * is refetched: the server is the truth after a burst, a failure, or a
 * publish that landed mid-burst.
 */
export function useUpdateSchemaExtensionFieldExposure(extensionId: string) {
  const t = useGT();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = fieldExposureKey(extensionId);
  const mutationKey = [QUERY_KEY_BASE, 'update-field-exposure', extensionId];
  const isLastPendingSave = () => queryClient.isMutating({ mutationKey }) <= 1;

  function showToggle(toggle: ExposureToggle) {
    queryClient.setQueryData<SchemaExtensionFieldExposureResponse>(
      queryKey,
      (previous) =>
        previous && {
          ...previous,
          fieldExposure: applyExposureToggle(previous.fieldExposure, toggle),
        },
    );
  }

  return useMutation({
    mutationKey,
    scope: { id: `schema-extension-exposure:${extensionId}` },
    mutationFn: ({ fieldId, exposure, value }: ExposureToggle) =>
      rpcClient.core.schemaExtensions.updateFieldExposure({
        extensionId,
        fieldId,
        exposure,
        value,
      }),
    onMutate: async (toggle) => {
      await queryClient.cancelQueries({ queryKey });
      showToggle(toggle);
    },
    onError: (error, toggle) => {
      showToast({
        title: t('Could not save exposure for {label}', {
          label: toggle.label,
        }),
        message: error instanceof Error ? error.message : undefined,
        color: 'error',
      });
      // Queued saves still carry newer clicks; the refetch settles those.
      if (isLastPendingSave()) showToggle({ ...toggle, value: !toggle.value });
    },
    onSettled: async () => {
      if (isLastPendingSave())
        await queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Publishing syncs a registered extension's table, so its status (physical
 * columns) and provisioned fields (the exposure map's keys) change with it.
 */
export function useInvalidateSchemaExtensions() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BASE] });
}
